import { useCallback, useEffect, useState } from 'react';
import {
  IconGlobe,
  IconBolt,
  IconSignal,
  IconUsers,
  IconChart,
} from './icons';
import { RegionFlag } from './RegionFlag';
import { api, type PortalRegionItem } from '../services/api';
import {
  diagnoseRegion,
  isRegionUp,
  regionStatusLabel,
  type RegionDiagResult,
  type RegionDiagStatus,
} from '../utils/regionDiagnostics';

interface RegionDiagnosticsPanelProps {
  deviceLimit: number;
  onSelectRegion?: (code: string) => void;
  selectedCode?: string;
}

type DiagMap = Record<string, RegionDiagResult | { status: 'checking' }>;

function speedTone(pct: number | null): 'good' | 'mid' | 'bad' | 'muted' {
  if (pct == null) return 'muted';
  if (pct >= 55) return 'good';
  if (pct >= 30) return 'mid';
  return 'bad';
}

function statusClass(status: RegionDiagStatus | 'checking'): string {
  if (status === 'online') return 'is-online';
  if (status === 'limited') return 'is-limited';
  if (status === 'offline') return 'is-offline';
  return 'is-muted';
}

export function RegionDiagnosticsPanel({
  deviceLimit,
  onSelectRegion,
  selectedCode,
}: RegionDiagnosticsPanelProps) {
  const [regions, setRegions] = useState<PortalRegionItem[]>([]);
  const [diag, setDiag] = useState<DiagMap>({});
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runProbes = useCallback(async (list: PortalRegionItem[]) => {
    if (list.length === 0) {
      setDiag({});
      return;
    }
    setProbing(true);
    setDiag(Object.fromEntries(list.map((r) => [r.code, { status: 'checking' as const }])));

    const next: DiagMap = {};
    await Promise.all(
      list.map(async (region) => {
        next[region.code] = await diagnoseRegion(region.probe_url, region.capacity_mbps);
      }),
    );
    setDiag(next);
    setProbing(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.listVpnRegions();
    if (res.success && res.data) {
      setRegions(res.data);
      setLoading(false);
      void runProbes(res.data);
    } else {
      setError(res.error?.message ?? 'Не удалось загрузить регионы');
      setRegions([]);
      setLoading(false);
    }
  }, [runProbes]);

  useEffect(() => {
    void load();
  }, [load]);

  const upCount = regions.filter((r) => {
    const d = diag[r.code];
    return d && isRegionUp(d.status);
  }).length;
  const uptimePct =
    regions.length > 0 ? Math.min(99.99, Math.round((upCount / regions.length) * 10000) / 100) : null;
  const capacityLabel = `${Math.round((regions[0]?.capacity_mbps ?? 1000) / 1000) || 1} Гбит`;

  return (
    <div className="region-dash">
      <div className="region-panel region-panel-list">
        <div className="region-panel-head">
          <div className="region-panel-title">
            <span className="region-panel-title-icon">
              <IconGlobe size={18} />
            </span>
            <h3>Ваши регионы</h3>
          </div>
          {probing ? <span className="region-panel-hint muted">Проверка…</span> : null}
        </div>

        {error ? <p className="error-text" style={{ margin: 0 }}>{error}</p> : null}
        {loading ? <p className="muted" style={{ margin: 0 }}>Загрузка регионов…</p> : null}

        {!loading && !error && regions.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            Нет доступных регионов для вашего тарифа.
          </p>
        ) : null}

        {!loading && regions.length > 0 ? (
          <div className="region-table-wrap">
            <table className="region-table">
              <thead>
                <tr>
                  <th>Страна / Регион</th>
                  <th>Скорость</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((region) => {
                  const d = diag[region.code];
                  const status = d?.status ?? 'checking';
                  const speedPct = d && 'speedPct' in d ? d.speedPct : null;
                  const pingMs = d && 'pingMs' in d ? d.pingMs : null;
                  const tone = speedTone(speedPct);
                  const active = selectedCode?.toLowerCase() === region.code.toLowerCase();

                  return (
                    <tr
                      key={region.code}
                      className={active ? 'is-active' : undefined}
                      onClick={onSelectRegion ? () => onSelectRegion(region.code) : undefined}
                      style={onSelectRegion ? { cursor: 'pointer' } : undefined}
                    >
                      <td>
                        <div className="region-name-cell">
                          <RegionFlag code={region.code} label={region.name} size={32} />
                          <div>
                            <div className="region-name">{region.name}</div>
                            <div className="region-city muted">
                              {region.city || region.code.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="region-speed-cell">
                          <div className={`region-speed-bar tone-${tone}`}>
                            <div
                              className="region-speed-fill"
                              style={{ width: `${status === 'checking' ? 15 : speedPct ?? 0}%` }}
                            />
                          </div>
                          <span className="region-ping">
                            {status === 'checking'
                              ? '…'
                              : pingMs != null
                                ? `${pingMs} ms`
                                : '—'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`region-status-pill ${statusClass(status)}`}>
                          <span className="region-status-dot" />
                          {regionStatusLabel(status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="region-panel region-panel-stats">
        <div className="region-panel-head">
          <div className="region-panel-title">
            <span className="region-panel-title-icon">
              <IconChart size={18} />
            </span>
            <h3>Статистика</h3>
          </div>
        </div>
        <div className="region-stats-grid">
          <div className="region-stat-tile">
            <span className="region-stat-icon">
              <IconBolt size={18} />
            </span>
            <div className="region-stat-copy">
              <strong>{capacityLabel}</strong>
              <span className="muted">Скорость</span>
            </div>
          </div>
          <div className="region-stat-tile">
            <span className="region-stat-icon">
              <IconGlobe size={18} />
            </span>
            <div className="region-stat-copy">
              <strong>{regions.length || '—'}</strong>
              <span className="muted">Регионов</span>
            </div>
          </div>
          <div className="region-stat-tile">
            <span className="region-stat-icon">
              <IconSignal size={18} />
            </span>
            <div className="region-stat-copy">
              <strong>{uptimePct != null ? `${uptimePct}%` : '—'}</strong>
              <span className="muted">Аптайм</span>
            </div>
          </div>
          <div className="region-stat-tile">
            <span className="region-stat-icon">
              <IconUsers size={18} />
            </span>
            <div className="region-stat-copy">
              <strong>{deviceLimit}</strong>
              <span className="muted">Устройств</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
